import { useEffect } from "react";
import type { Character } from "@/types";

interface CharacterModalProps {
  character: Character | null;
  onClose: () => void;
}

export default function CharacterModal({ character, onClose }: CharacterModalProps) {
  useEffect(() => {
    if (!character) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [character, onClose]);

  if (!character) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background-950/80 p-4 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${character.name} 角色详情`}
    >
      <div
        className="panel relative w-full max-w-2xl overflow-hidden rounded-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-md bg-background-950/70 text-foreground-200 transition-colors hover:text-accent-400"
          aria-label="关闭"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="flex flex-col sm:flex-row">
          <div className="h-64 w-full shrink-0 overflow-hidden sm:h-auto sm:w-56">
            <img
              src={character.avatar}
              alt={`${character.name}（${character.actor} 饰）角色形象`}
              title={`${character.name} - 激情丛林电影角色`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="flex-1 p-6">
            <span className="font-label text-xs uppercase tracking-[0.2em] text-accent-500">
              {character.role}
            </span>
            <h3 className="mt-2 font-heading text-2xl font-bold text-foreground-50">
              {character.name}
            </h3>
            <p className="mt-1 flex items-center gap-1.5 font-label text-sm text-foreground-400">
              <i className="ri-user-star-line"></i>
              由 {character.actor} 饰演
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-300">{character.bio}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {character.traits.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-secondary-100/10 px-3 py-1 font-label text-xs text-secondary-300"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}